import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import { fetchApi } from "common/utils/Api";

export type Device = {
  guid: string;
  name: string;
  enabled: boolean;
};

export type DeviceCreatePayload = {
  name: string;
  token: string;
};

export type DeviceSliceState = {
  loading: "idle" | "pending";
  error: boolean;
  currentDevice?: Device;
};

// Async thunk for registering a device
export const registerDevice = createAsyncThunk<
  Device,
  DeviceCreatePayload,
  { rejectValue: string }
>("device/register", async (deviceData, { rejectWithValue }) => {
  try {
    const response = await fetchApi("devices", {
      method: "POST",
      body: JSON.stringify(deviceData),
    });
    return response as Device;
  } catch (error) {
    return rejectWithValue("Failed to register device");
  }
});

// Async thunk for getting device list
export const getDevices = createAsyncThunk<
  Device[],
  void,
  { rejectValue: string }
>("device/getDevices", async (_, { rejectWithValue }) => {
  try {
    const response = await fetchApi("devices", {
      method: "GET",
    });
    return response as Device[];
  } catch (error) {
    return rejectWithValue("Failed to get devices");
  }
});

const initialState: DeviceSliceState = {
  loading: "idle",
  error: false,
  currentDevice: undefined,
};

const deviceSlice = createSlice({
  name: "device",
  initialState,
  reducers: {
    resetDevice: () => initialState,
  },
  extraReducers: (builder) => {
    builder
      .addCase(registerDevice.pending, (state) => {
        state.loading = "pending";
        state.error = false;
      })
      .addCase(registerDevice.fulfilled, (state, action) => {
        state.loading = "idle";
        state.currentDevice = action.payload;
        state.error = false;
      })
      .addCase(registerDevice.rejected, (state) => {
        state.loading = "idle";
        state.error = true;
      })
      .addCase(getDevices.pending, (state) => {
        state.loading = "pending";
        state.error = false;
      })
      .addCase(getDevices.fulfilled, (state) => {
        state.loading = "idle";
        state.error = false;
      })
      .addCase(getDevices.rejected, (state) => {
        state.loading = "idle";
        state.error = true;
      });
  },
});

export const { resetDevice } = deviceSlice.actions;
export const deviceReducer = deviceSlice.reducer;
